import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingCart, Heart } from "lucide-react";
import { useCart } from "../../hooks/useCart";
import { useSiteImages } from "../../hooks/useSiteImages";

// Lane 4 For All Kids Foundation, Inc.
const FOUNDATION_URL = "https://lane4foundation.com/";

const DONATE_LABEL = "Donate to Lane 4 For All Kids Foundation";

/** Renders the uploaded button artwork as the entire button. Until one is
 *  uploaded it falls back to a plain red pill, so it is never broken. */
const DonateButton = ({ image, imgClass, wrapClass, pillClass, iconSize, text, testId }) => (
  <a
    href={FOUNDATION_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    aria-label={DONATE_LABEL}
    className={image ? wrapClass : pillClass}
  >
    {image ? (
      <img src={image} alt={DONATE_LABEL} className={imgClass} />
    ) : (
      <>
        <Heart size={iconSize} fill="currentColor" /> {text}
      </>
    )}
  </a>
);

const Navigation = () => {
  const { getImage } = useSiteImages();
  const donateImage = getImage("donate_button_image");
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "E-Tickets", path: "/e-tickets" },
    { name: "Custom Medals", path: "/medals" },
    { name: "Events", path: "/events" },
    { name: "Merch", path: "/merch" },
    { name: "Reviews", path: "/reviews" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-[#0F2137]/90 backdrop-blur-sm"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <div className={`rounded-lg transition-all ${scrolled ? '' : 'bg-white/95 p-1.5'}`}>
              <img src={getImage("logo")} alt="Lane4" className="h-12 w-auto" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`link-hover font-medium transition-colors ${
                  scrolled ? "text-[#0F2137]" : "text-white"
                } hover:text-[#D90429]`}
              >
                {link.name}
              </Link>
            ))}
            <DonateButton
              image={donateImage}
              testId="donate-button"
              imgClass="h-10 w-auto"
              wrapClass="inline-flex items-center flex-shrink-0 hover:opacity-90 transition-opacity"
              pillClass="inline-flex items-center gap-2 bg-[#D90429] text-white font-semibold rounded-full px-5 py-2.5 whitespace-nowrap hover:bg-[#B00322] transition-colors shadow-sm"
              iconSize={16}
              text="Donate"
            />
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-button"
              className={`relative p-2 rounded-full transition-colors ${
                scrolled ? "hover:bg-gray-100" : "hover:bg-white/10"
              }`}
            >
              <ShoppingCart className={scrolled ? "text-[#0F2137]" : "text-white"} size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D90429] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 sm:gap-4 lg:hidden">
            <DonateButton
              image={donateImage}
              testId="mobile-donate-button"
              imgClass="h-8 w-auto"
              wrapClass="inline-flex items-center flex-shrink-0 hover:opacity-90 transition-opacity"
              pillClass="inline-flex items-center gap-1.5 bg-[#D90429] text-white font-semibold text-sm rounded-full px-3.5 py-2 whitespace-nowrap hover:bg-[#B00322] transition-colors"
              iconSize={14}
              text="Donate"
            />
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="mobile-cart-button"
              className="relative p-2"
            >
              <ShoppingCart className={scrolled ? "text-[#0F2137]" : "text-white"} size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D90429] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-button"
              className="p-2"
            >
              {isOpen ? (
                <X className={scrolled ? "text-[#0F2137]" : "text-white"} size={28} />
              ) : (
                <Menu className={scrolled ? "text-[#0F2137]" : "text-white"} size={28} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white shadow-lg overflow-hidden"
          >
            <div className="container-custom py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-4 py-3 text-[#0F2137] hover:bg-[#F8F9FA] rounded-lg font-medium"
                >
                  {link.name}
                </Link>
              ))}
              <DonateButton
                image={donateImage}
                testId="mobile-menu-donate"
                imgClass="h-12 w-auto max-w-full"
                wrapClass="mt-3 flex justify-center hover:opacity-90 transition-opacity"
                pillClass="mt-3 flex items-center justify-center gap-2 bg-[#D90429] text-white font-semibold rounded-lg px-4 py-3 hover:bg-[#B00322] transition-colors"
                iconSize={18}
                text="Donate to Lane 4 For All Kids"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
